/**
 * Step to log into an existing account for Clever Library users.
 */
import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import type {LoginUserData} from "@khan/auth-shared";
import {useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {
    completePostLogin,
    linkClever,
    logoutCleverUser,
} from "./login-actions.ts";
import U13ErrorStep from "./u13-error-step.tsx";

interface Props {
    abandonLoginStep: string;
    onAbandonLogin: () => void;
    onAccountLink: () => void;
    isDistrictUser: boolean;
    transferToken: string | null | undefined;
    user: LoginUserData | null | undefined;
    /**
     * Optional identifier to use for the dialog title.
     *
     * Usefult for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

export default function StudentConfirmAccountLinkStep({
    abandonLoginStep,
    onAbandonLogin,
    onAccountLink,
    isDistrictUser,
    transferToken,
    user,
    titleId,
}: Props) {
    const {gqlClient} = useApolloGqlClient();
    const location = useLocation();
    const [submitting, setSubmitting] = React.useState(false);
    const [loggingOut, setLoggingOut] = React.useState(false);

    const completeAccountLink = () => {
        setSubmitting(true);
        onAccountLink();
        if (transferToken) {
            return linkClever(gqlClient, {transferToken}).then(() => {
                completePostLogin(
                    gqlClient,
                    false,
                    "/",
                    user && {user, isFirstLogin: false},
                    location,
                );
            });
        }
        completePostLogin(gqlClient, false, "/");
    };

    const abandonLogin = () => {
        setLoggingOut(true);
        onAbandonLogin();
        logoutCleverUser(location, abandonLoginStep);
    };

    const renderConfirmation = () => (
        <View>
            <Strut size={spacing.medium_16} />
            <HeadingMedium id={titleId}>
                {t`Can we use your existing account?`}
            </HeadingMedium>
            <Strut size={spacing.large_24} />
            <Body>
                {t`By using your existing account and adding your Clever ID to it, you will see all your existing progress in this one school account. And it will allow your teachers to see all your activity.`}
            </Body>
            <Strut size={spacing.large_24} />
            <Button
                onClick={completeAccountLink}
                disabled={submitting || loggingOut}
                spinner={submitting}
            >
                {t`Yes, use my existing account`}
            </Button>
            <Strut size={spacing.small_12} />
            <Button
                kind="secondary"
                onClick={abandonLogin}
                disabled={submitting}
                spinner={loggingOut}
            >
                {t`No, create a new account`}
            </Button>
        </View>
    );

    let age;
    let nickname;

    if (user) {
        age = user.age;
        nickname = user.nickname;
    }

    if (isDistrictUser) {
        return renderConfirmation();
    }

    if (!age || !nickname) {
        return (
            <U13ErrorStep
                titleId={titleId}
                nickname="User"
                ageMissing={true}
                onSignUpClick={abandonLogin}
            />
        );
    }

    if (age < 13) {
        return (
            <U13ErrorStep
                titleId={titleId}
                nickname={nickname}
                ageMissing={false}
                onSignUpClick={abandonLogin}
            />
        );
    }
    return renderConfirmation();
}
